import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { isAuthenticatedViaCookie, getCookie, getUserInfoFromLocalStorage } from '../../utils/utils';
import { 
  fetchSubscriptionsApi, 
  pauseSubscriptionApi, 
  resumeSubscriptionApi, 
  cancelSubscriptionApi 
} from '../../utils/apis';
import LoadingOverlay from '../insurance/LoadingOverlay';
import Sidebar from '../linkpayment/Sidebar';
import MobileMenuButton from '../linkpayment/MobileMenuButton';
import '../linkpayment/LinkPayment.css';

interface Subscription {
  subscriptionId: string;
  amount: number;
  frequency: string;
  startDate: string;
  endDate: string;
  status: string;
  isPaused: boolean | null;
  isActive: boolean;
  nextPaymentDate: string;
  lastPaymentDate: string | null;
  totalPayments: number | null;
  createdAt: string;
  updatedAt: string;
  metadata: any;
  success: boolean | null;
  message: string | null;
  error: string | null;
  isCancelled: boolean | null;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const Subscriptions: React.FC = () => {
  const { isAuthenticated } = useAuthContext();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    action: 'pause' | 'cancel' | null;
    subscriptionId: string | null;
  }>({
    isOpen: false,
    action: null,
    subscriptionId: null
  });

  // Check authentication using cookie
  const isUserAuthenticated = (): boolean => {
    const cookieAuth = isAuthenticatedViaCookie();
    const localAuth = localStorage.getItem('isAuthenticated') === 'true';
    const contextAuth = isAuthenticated;
    return cookieAuth || localAuth || contextAuth;
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  useEffect(() => {
    const authStatus = isUserAuthenticated();
    
    if (!authStatus) {
      navigate('/');
      return;
    }

    // Get user info from local storage
    setUserInfo(getUserInfoFromLocalStorage());

    fetchSubscriptions();
  }, [isAuthenticated, navigate]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchSubscriptionsApi();
      
      if (response && Array.isArray(response)) {
        setSubscriptions(response);
      } else {
        setSubscriptions([]);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch subscriptions');
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (subscriptionId: string, action: 'pause' | 'resume' | 'cancel') => {
    try {
      setActionLoading(subscriptionId);
      setError(null);
      
      let response;
      switch (action) {
        case 'pause':
          response = await pauseSubscriptionApi(subscriptionId);
          break;
        case 'resume':
          response = await resumeSubscriptionApi(subscriptionId);
          break;
        case 'cancel':
          response = await cancelSubscriptionApi(subscriptionId);
          break;
      }
      
      if (response && response.success) {
        // Refresh the subscriptions list
        fetchSubscriptions();
      } else {
        setError(`Failed to ${action} subscription`);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || `Failed to ${action} subscription`);
    } finally {
      setActionLoading(null);
    }
  };

  const openConfirmationModal = (action: 'pause' | 'cancel', subscriptionId: string) => {
    setConfirmationModal({
      isOpen: true,
      action,
      subscriptionId
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal({
      isOpen: false,
      action: null,
      subscriptionId: null
    });
  };

  const confirmAction = () => {
    if (confirmationModal.action && confirmationModal.subscriptionId) {
      handleAction(confirmationModal.subscriptionId, confirmationModal.action);
      closeConfirmationModal();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'paused':
        return 'text-orange-600 bg-orange-100';
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getActionButton = (subscription: Subscription) => {
    const isLoading = actionLoading === subscription.subscriptionId;
    
    if (subscription.isCancelled) {
      return <span className="text-gray-500 text-sm">Cancelled</span>;
    }
    
    // If isActive is false, subscription is paused - show resume option
    if (!subscription.isActive) {
      return (
        <div className="flex gap-2">
          <button
            onClick={() => handleAction(subscription.subscriptionId, 'resume')}
            disabled={isLoading}
            className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
          >
            {isLoading ? 'Resuming...' : 'Resume'}
          </button>
          <button
            onClick={() => openConfirmationModal('cancel', subscription.subscriptionId)}
            disabled={isLoading}
            className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      );
    }
    
    // If isActive is true, subscription is active - show pause and cancel options
    return (
      <div className="flex gap-2">
        <button
          onClick={() => openConfirmationModal('pause', subscription.subscriptionId)}
          disabled={isLoading}
          className="px-3 py-1 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50"
        >
          Pause
        </button>
        <button
          onClick={() => openConfirmationModal('cancel', subscription.subscriptionId)}
          disabled={isLoading}
          className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getConfirmationModalProps = () => {
    if (!confirmationModal.action || !confirmationModal.subscriptionId) return null;

    const subscription = subscriptions.find(s => s.subscriptionId === confirmationModal.subscriptionId);
    const amount = subscription ? formatAmount(subscription.amount) : '';

    if (confirmationModal.action === 'pause') {
      return {
        title: 'Pause Subscription',
        message: `Are you sure you want to pause this subscription? The subscription will be paused and no further payments will be processed until you resume it.`,
        confirmText: 'Pause Subscription',
        cancelText: 'Keep Active'
      };
    } else if (confirmationModal.action === 'cancel') {
      return {
        title: 'Cancel Subscription',
        message: `Are you sure you want to cancel this subscription? This action cannot be undone and the subscription will be permanently cancelled.`,
        confirmText: 'Cancel Subscription',
        cancelText: 'Keep Subscription'
      };
    }

    return null;
  };

  if (loading) {
    return (
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Sidebar */}
        <Sidebar 
          userInfo={userInfo} 
          isOpen={isSidebarOpen} 
          onClose={closeSidebar} 
        />
        
        {/* Mobile Header */}
        <div className="lg:hidden bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
          <div className="px-4 py-3 flex items-center justify-between flex-row-reverse">
            <div className="flex items-center">
              <h1 className="text-lg font-bold text-gray-900">St. Luke Hospital</h1>
            </div>
            <MobileMenuButton isOpen={isSidebarOpen} onToggle={toggleSidebar} />
          </div>
        </div>
        
        <div className="w-full">
          <LoadingOverlay />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row min-h-screen">
      {/* Sidebar */}
      <Sidebar 
        userInfo={userInfo} 
        isOpen={isSidebarOpen} 
        onClose={closeSidebar} 
      />
      
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between flex-row-reverse">
          <div className="flex items-center">
            <h1 className="text-lg font-bold text-gray-900">St. Luke Hospital</h1>
          </div>
          <MobileMenuButton isOpen={isSidebarOpen} onToggle={toggleSidebar} />
        </div>
      </div>
      
      <div className="w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Your Subscriptions</h2>
            <div className="text-sm text-gray-600">
              Total: {subscriptions.length} subscription{subscriptions.length !== 1 ? 's' : ''}
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {subscriptions.length === 0 ? (
            <div className="text-center py-12">
              <div className="mb-6">
                <svg 
                  className="mx-auto h-16 w-16 text-gray-400" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor" 
                  aria-hidden="true"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={1.5} 
                    d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" 
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Subscriptions</h3>
              <p className="text-gray-500 mb-6">You don't have any active subscriptions yet. Start by creating your first subscription!</p>
              <button
                onClick={() => navigate('/payments')}
                className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white ${theme["button-bg"]} hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              >
                Create Your First Subscription
                <svg className="ml-2 -mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {subscriptions.map((subscription) => (
                <div key={subscription.subscriptionId} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {formatAmount(subscription.amount)}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(subscription.status)}`}>
                          {subscription.status}
                        </span>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                          {subscription.frequency}
                        </span>
                        {subscription.isPaused && (
                          <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                            Paused
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Subscription ID:</span>
                          <div className="mt-1 font-mono text-xs text-gray-900 break-all">
                            #{subscription.subscriptionId}
                          </div>
                        </div>
                        <div>
                          <span className="font-medium">Started:</span>
                          <div className="mt-1 text-gray-900">
                            {formatDate(subscription.startDate)}
                          </div>
                        </div>
                        <div>
                          <span className="font-medium">Next Payment:</span>
                          <div className="mt-1 text-gray-900">
                            {subscription.nextPaymentDate ? formatDate(subscription.nextPaymentDate) : 'N/A'}
                          </div>
                        </div>
                        <div>
                          <span className="font-medium">Total Payments:</span>
                          <div className="mt-1 text-gray-900">
                            {subscription.totalPayments || 0}
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                          per {subscription.frequency.toLowerCase()}
                        </div>
                        {getActionButton(subscription)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
      </div>

      {getConfirmationModalProps() && (
        <ConfirmationModal
          isOpen={confirmationModal.isOpen}
          onClose={closeConfirmationModal}
          onConfirm={confirmAction}
          title={getConfirmationModalProps()!.title}
          message={getConfirmationModalProps()!.message}
          confirmText={getConfirmationModalProps()!.confirmText}
          cancelText={getConfirmationModalProps()!.cancelText}
        />
      )}
    </div>
  );
};

export default Subscriptions; 